import { ReactNode } from 'react';
import { useCommands, useLogger, useRedirectToMainApp, useServices } from '@packages/shared/core/common/hooks';
import { useQuizStep } from '@packages/shared/core/quiz/hooks';
import { ProcessingWithRedirectStepData, QuizScreenProps } from '@packages/shared/core/quiz/entities';
import { useExperts } from '@packages/shared/core/experts/hooks';
import { useChat } from '@packages/shared/core/chat/hooks';
import { stringifyQueryParams } from '@packages/shared/core/common/utils/query';

type ProcessingQuizStepWithRedirectProps = {
  id: string;
  renderQuizScreen: (stepData: QuizScreenProps) => ReactNode;
};

const ProcessingQuizStepWithRedirect = ({ id, renderQuizScreen }: ProcessingQuizStepWithRedirectProps) => {
  const { chatService } = useServices();
  const logger = useLogger();
  const { stepData, flowName } = useQuizStep(id);

  const { selectedExpert } = useExperts({ flowName });
  const { createCommand } = useCommands();

  const expertId = selectedExpert ? selectedExpert.id : '';
  const { outgoingMessage } = useChat(expertId);

  const { redirectToExpertsPage } = useRedirectToMainApp();

  if (!stepData) {
    // TODO add fallback
    logger.error(new TypeError(`Data for ${id} step not found`));
    return null;
  }

  const onProcessingFinished = async () => {
    if (!selectedExpert) {
      redirectToExpertsPage();
      return;
    }

    const { chatId } = await chatService.createChat(selectedExpert.id);

    const commandParams = stringifyQueryParams({
      chatId,
      text: outgoingMessage,
    });
    await createCommand(`writeMessage?${commandParams}`);
  };

  return (
    <>
      {renderQuizScreen({
        onFinished: onProcessingFinished,
        ...(stepData as ProcessingWithRedirectStepData),
      })}
    </>
  );
};

export default ProcessingQuizStepWithRedirect;
